import React, { useCallback, useEffect, useState } from 'react'
import { Box, Flex, Input, InputGroup, InputLeftElement, Select, Text, useColorModeValue, Button, Collapse, Icon } from '@chakra-ui/react'
import { SearchIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'

// Implementando debounce para melhorar performance
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export const FilterBar = React.memo(({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [responsavel, setResponsavel] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const bgColor = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const hoverBg = useColorModeValue('gray.50', 'gray.600')

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  useEffect(() => {
    onFilterChange({
      searchTerm: debouncedSearchTerm,
      responsavel,
    })
  }, [debouncedSearchTerm, responsavel, onFilterChange])

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value)
  }, [])

  const handleResponsavelChange = useCallback((e) => {
    setResponsavel(e.target.value)
  }, [])

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  return (
    <Box bg={bgColor} borderRadius="md" boxShadow="sm" mb={4} borderWidth="1px" borderColor={borderColor}>
      {/* Cabeçalho do filtro - sempre visível */}
      <Flex
        p={3}
        justifyContent="space-between"
        alignItems="center"
        onClick={toggleOpen}
        cursor="pointer"
        _hover={{ bg: hoverBg }}
        transition="background 0.2s"
      >
        <Flex alignItems="center" gap={2}>
          <Icon as={SearchIcon} color="blue.500" />
          <Text fontWeight="medium">Filtrar Chamados</Text>

          {/* Indicadores de filtros ativos */}
          {(debouncedSearchTerm || responsavel) && <Box bg="blue.500" borderRadius="full" w="8px" h="8px" ml={2} title="Filtros ativos" />}
        </Flex>

        <Button size="sm" rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />} variant="ghost" colorScheme="blue">
          {isOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
        </Button>
      </Flex>

      {/* Conteúdo do filtro - colapsável */}
      <Collapse in={isOpen} animateOpacity>
        <Box p={4} borderTopWidth="1px" borderColor={borderColor}>
          <Flex flexDirection={{ base: 'column', md: 'row' }} gap={3} alignItems={{ base: 'stretch', md: 'flex-end' }}>
            <Box flex={2}>
              <Text fontSize="sm" mb={1}>
                Buscar por número ou solicitante
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input placeholder="Número do chamado ou nome do solicitante" value={searchTerm} onChange={handleSearchChange} variant="filled" />
              </InputGroup>
            </Box>

            <Box flex={1}>
              <Text fontSize="sm" mb={1}>
                Filtrar por responsável
              </Text>
              <Select value={responsavel} onChange={handleResponsavelChange} variant="filled" placeholder="Todos os responsáveis">
                <option value="FRONT">Front</option>
                <option value="BACK">Back</option>
                <option value="BANCO">Banco</option>
                <option value="SEM DEFINICAO">Sem definição</option>
              </Select>
            </Box>
          </Flex>

          {/* Área para resumo dos filtros */}
          {(debouncedSearchTerm || responsavel) && (
            <Flex mt={4} gap={2} flexWrap="wrap">
              <Text fontSize="sm" color="gray.500">
                Filtros ativos:
              </Text>
              {debouncedSearchTerm && (
                <Text fontSize="sm" fontWeight="medium">
                  Termo: "{debouncedSearchTerm}"
                </Text>
              )}
              {responsavel && (
                <Text fontSize="sm" fontWeight="medium">
                  Responsável: {responsavel}
                </Text>
              )}
            </Flex>
          )}
        </Box>
      </Collapse>
    </Box>
  )
})
